import { FairCandidateItemDto } from '@/api/generated';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { initials, parseUtc } from '@/views/admin/candidates/candidateStatus.ts';
import { StatusChip } from '@/views/admin/candidates/StatusChip.comp.tsx';
import { COLORS, formatNumber, SCORE_COLORS } from '@/views/admin/dashboard/dashboardTheme.ts';
import React from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { IoPersonAddOutline } from 'react-icons/io5';

const MAX_POINTS = 850;

interface Props {
  candidates: FairCandidateItemDto[];
  isLoading: boolean;
  language: string;
  onEdit: (candidate: FairCandidateItemDto) => void;
  onOpen: (candidate: FairCandidateItemDto) => void;
  openingEmail?: string;
}

export const FairCandidatesList: React.FC<Props> = ({
  candidates,
  isLoading,
  language,
  onEdit,
  onOpen,
  openingEmail,
}) => {
  const { t } = useTranslationWithPrefix('views.admin');
  const dateTime = new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' });
  const columns = [
    { key: 'candidate', label: t('fairs.list.columns.candidate'), width: '26%' },
    { key: 'fair', label: t('fairs.list.columns.fair'), width: '16%' },
    { key: 'status', label: t('fairs.list.columns.status'), width: '16%' },
    { key: 'score', label: t('fairs.list.columns.score'), width: '13%' },
    { key: 'added', label: t('fairs.list.columns.added'), width: '13%' },
    { key: 'registered', label: t('fairs.list.columns.registered'), width: '11%' },
    { key: 'actions', label: '', width: '5%' },
  ];

  return (
    <div
      className="overflow-x-auto"
      style={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 200ms' }}
    >
      <table
        className="w-full"
        style={{ borderCollapse: 'collapse', minWidth: 940, tableLayout: 'fixed' }}
      >
        <colgroup>
          {columns.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: `1px solid ${COLORS.grid}` }}>
            {columns.map(column => (
              <th
                className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider"
                key={column.key}
                scope="col"
                style={{ color: COLORS.inkMuted }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {candidates.map(candidate => {
            const registered = !!candidate.candidateId;
            const registeredAt = parseUtc(candidate.registeredAt);
            const addedAt = parseUtc(candidate.createdAt);
            const opening = candidate.email === openingEmail;
            return (
              <tr
                className={registered ? 'cursor-pointer transition-colors hover:bg-[#fafafa]' : ''}
                key={candidate.email}
                onClick={registered ? () => onOpen(candidate) : undefined}
                onKeyDown={
                  registered ? event => event.key === 'Enter' && onOpen(candidate) : undefined
                }
                style={{
                  borderBottom: `1px solid ${COLORS.grid}`,
                  opacity: opening ? 0.6 : 1,
                }}
                tabIndex={registered ? 0 : undefined}
                title={registered ? t('fairs.list.openProfile') : undefined}
              >
                <td className="px-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      aria-hidden
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                      style={
                        registered
                          ? { backgroundColor: COLORS.ink, color: '#fff' }
                          : { border: '1px dashed #b3b3b3', color: COLORS.inkMuted }
                      }
                    >
                      {registered ? (
                        initials(candidate.firstName, candidate.lastName)
                      ) : (
                        <IoPersonAddOutline />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-bold" style={{ color: COLORS.ink }}>
                        {candidate.lastName
                          ? `${candidate.firstName ?? ''} ${candidate.lastName}`.trim()
                          : candidate.email}
                      </div>
                      {candidate.lastName && (
                        <div className="truncate text-xs" style={{ color: COLORS.inkMuted }}>
                          {candidate.email}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-sm">
                  <div
                    className="truncate"
                    style={{ color: candidate.note ? COLORS.ink : COLORS.inkMuted }}
                    title={candidate.note}
                  >
                    {candidate.note ?? '—'}
                  </div>
                </td>
                <td className="px-3 py-3">
                  {candidate.status ? (
                    <StatusChip status={candidate.status} />
                  ) : (
                    <span
                      className="inline-flex items-center px-2 py-1 text-xs font-bold"
                      style={{ border: '1px dashed #b3b3b3', borderRadius: 4, color: '#595959' }}
                    >
                      {t('fairs.list.notRegistered')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {candidate.quizPoints && candidate.quizScore ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <strong style={{ color: COLORS.ink }}>
                          {formatNumber(candidate.quizPoints, language)}
                        </strong>
                        <span className="truncate text-xs" style={{ color: COLORS.inkSecondary }}>
                          {t(`table.quizScores.${candidate.quizScore}`)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full" style={{ backgroundColor: COLORS.grid }}>
                        <div
                          className="h-full"
                          style={{
                            backgroundColor: SCORE_COLORS[candidate.quizScore],
                            width: `${Math.min(100, (candidate.quizPoints / MAX_POINTS) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <span style={{ color: COLORS.inkMuted }}>—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs" style={{ color: COLORS.inkSecondary }}>
                  <div>{addedAt ? dateTime.format(addedAt) : '—'}</div>
                  {candidate.createdBy && (
                    <div
                      className="truncate"
                      style={{ color: COLORS.inkMuted }}
                      title={candidate.createdBy}
                    >
                      {t('fairs.list.addedBy', { name: candidate.createdBy })}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-xs" style={{ color: COLORS.inkSecondary }}>
                  {registeredAt ? dateTime.format(registeredAt) : '—'}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    aria-label={`${t('fairs.list.edit')}: ${candidate.email}`}
                    className="inline-flex h-8 w-8 cursor-pointer items-center justify-center bg-transparent"
                    onClick={event => {
                      event.stopPropagation();
                      onEdit(candidate);
                    }}
                    style={{ border: '1px solid #ccc', color: COLORS.ink }}
                    title={t('fairs.list.edit')}
                    type="button"
                  >
                    <FaPencilAlt aria-hidden size={12} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!isLoading && candidates.length === 0 && (
        <p className="m-0 py-10 text-center text-sm" style={{ color: COLORS.inkMuted }}>
          {t('fairs.list.empty')}
        </p>
      )}
    </div>
  );
};
